import { decryptUserData, loadEncryptedUserData } from "../state/utils/configUtils";

/**
 * Gets the current user's role ID from encrypted data or session storage
 * @returns The user's role ID as a number, or null if not found
 */
export const getUserRole = (): number | null => {
  try {
    if (typeof window === "undefined") return null;
    
    // Try to get from encrypted data
    const encryptedUserData = loadEncryptedUserData();
    if (encryptedUserData) {
      const decryptedData = decryptUserData(encryptedUserData);
      if (decryptedData) {
        const roleId = decryptedData.roleid || 
                      decryptedData.roleId || 
                      decryptedData.role_id ||
                      decryptedData.usergroupid ||
                      decryptedData.usergroupId;
        if (roleId !== undefined && roleId !== null) {
          return typeof roleId === "number" ? roleId : Number(roleId);
        }
      }
    }
    
    // Fallback: try sessionStorage (if stored there)
    const roleFromStorage = sessionStorage.getItem("role_id") || 
                            sessionStorage.getItem("usergroupid");
    if (roleFromStorage) {
      return Number(roleFromStorage);
    }
    
    return null;
  } catch (error) {
    console.error("Error getting user role:", error);
    return null;
  }
};

/**
 * Checks if a component should be visible based on visibility array
 * Supports both new `visibility` pattern and legacy `visibleForRoles` for backward compatibility
 * 
 * @param visibility - Array of role IDs that can see this component (e.g., [1, 2])
 * @param visibleForRoles - Legacy parameter for backward compatibility
 * @returns true if component should be visible, false otherwise
 */
export const isComponentVisible = (
  visibility?: number[], 
  visibleForRoles?: number[] // Legacy support
): boolean => {
  // Use new pattern if available, fall back to legacy
  const roles = visibility || visibleForRoles;
  
  // If no visibility specified, show by default (backward compatibility)
  if (!roles || roles.length === 0) {
    return true;
  }
  
  const userRole = getUserRole();
  
  // If we can't determine role, hide by default for security
  if (userRole === null) {
    return false;
  }
  
  return roles.includes(userRole);
};

/** Resolve linked HRMS employee id from encrypted session / loginUser payload. */
export const getResolvedEmployeeId = (): string | undefined => {
  try {
    if (typeof window === "undefined") return undefined;
    const encryptedUserData = loadEncryptedUserData();
    if (!encryptedUserData) return undefined;
    const decryptedData = decryptUserData(encryptedUserData);
    if (!decryptedData) return undefined;

    const fromRoot =
      decryptedData.employee_id ??
      decryptedData.employeeId ??
      decryptedData.employeeid ??
      decryptedData.emp_id;

    if (fromRoot !== undefined && fromRoot !== null && String(fromRoot).trim() !== "") {
      return String(fromRoot).trim();
    }

    const loginUser = decryptedData.loginUser;
    if (loginUser && typeof loginUser === "object") {
      const lu = loginUser as Record<string, unknown>;
      const fromLogin =
        lu.employee_id ?? lu.employeeId ?? lu.employeeid ?? lu.emp_id;
      if (fromLogin !== undefined && fromLogin !== null && String(fromLogin).trim() !== "") {
        return String(fromLogin).trim();
      }
    }

    return undefined;
  } catch {
    return undefined;
  }
};

